<app-window-title title="Edit Patient"
                  [closable]="true"
                  (closed)="dialogRef.close()"></app-window-title>

<form class="edit-client-form"
      fxLayout="column"
      [formGroup]="clientForm"
      (ngSubmit)="onSubmit()">

  <!-- Error Message -->
  <div *ngIf="errorMessage"
       class="error-message">
    <mat-icon color="warn">
      warning
    </mat-icon>
    <span>
      {{errorMessage}}
    </span>
  </div>

  <!-- Basic Information Section -->
  <div class="form-section">
    <h3 class="section-title">
      Basic Information
    </h3>

    <div class="basic-info-layout"
         fxLayout="row"
         fxLayoutGap="20px"
         fxLayoutAlign="start center">
      <div class="patient-picture-container">
        <img *ngIf="data.patientPicture"
             class="patient-picture"
             alt="Patient Picture"
             [src]="data.patientPicture">
        <div *ngIf="!data.patientPicture"
             class="patient-picture-placeholder">
          <mat-icon>person</mat-icon>
        </div>
      </div>

      <div fxFlex
           fxLayout="column"
           fxLayoutGap="4px">
        <div fxLayout="row"
             fxLayout.lt-md="column"
             fxLayoutGap="16px">
          <div class="readonly-field-display"
               fxFlex>
            <label class="field-label">First name</label>
            <div class="field-value">{{clientForm.get('firstName')?.value || 'N/A'}}</div>
          </div>
          <div class="readonly-field-display"
               fxFlex>
            <label class="field-label">Last name</label>
            <div class="field-value">{{clientForm.get('lastName')?.value || 'N/A'}}</div>
          </div>
        </div>

        <div fxLayout="row"
             fxLayout.lt-md="column"
             fxLayoutGap="16px">
          <div class="readonly-field-display"
               fxFlex>
            <label class="field-label">Date of birth</label>
            <div class="field-value">
              {{ clientForm.get('dateOfBirth')?.value ? (clientForm.get('dateOfBirth')?.value | date:'MM/dd/yyyy') : 'N/A' }}
            </div>
          </div>
          <div class="readonly-field-display"
               fxFlex>
            <label class="field-label">Gender</label>
            <div class="field-value">{{clientForm.get('gender')?.value || 'N/A'}}</div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Contact Information Section -->
  <div class="form-section">
    <h3 class="section-title">
      Contact Information
    </h3>

    <div fxLayout="row"
         fxLayout.lt-md="column"
         fxLayoutGap="16px">
      <mat-form-field appearance="outline"
                      fxFlex>
        <mat-label>
          Spouse name
        </mat-label>
        <input type="text"
               formControlName="spouseName"
               matInput
               (input)="onFormFieldChange()">
      </mat-form-field>

      <mat-form-field appearance="outline"
                      fxFlex>
        <mat-label>
          Cell phone number
        </mat-label>
        <input type="tel"
               formControlName="cellPhone"
               matInput
               (input)="onFormFieldChange()">
      </mat-form-field>
    </div>

    <mat-form-field class="full-width"
                    appearance="outline">
      <mat-label>
        Email address
      </mat-label>
      <input type="email"
             formControlName="email"
             matInput
             (input)="onFormFieldChange()">
      <mat-error *ngIf="clientForm.get('email')?.invalid && clientForm.get('email')?.touched">
        Please enter a valid email address
      </mat-error>
    </mat-form-field>
  </div>

  <!-- Patient IDs Section -->
  <div class="form-section medical-records-section">
    <div class="section-header"
         fxLayout="row"
         fxLayoutAlign="space-between center">
      <div class="section-title-container">
        <h3 class="section-title">
          <mat-icon class="section-icon">
            medical_services
          </mat-icon>
          Patient IDs
        </h3>
        <p class="section-description">
          Manage medical record numbers across different facilities
        </p>
      </div>
      <button class="add-record-button"
              color="primary"
              type="button"
              mat-flat-button
              matTooltip="Add another medical record number"
              (click)="addMedicalRecordNumber()">
        <mat-icon matPrefix>
          add
        </mat-icon>
        Add Record
      </button>
    </div>

    <div class="medical-records-container">
      <div *ngFor="let patientID of patientIDs; let i = index"
           class="medical-record-entry"
           [class.existing-record]="patientID.isExisting"
           [class.primary-record]="i === 0">

        <!-- Record Meta: badge + delete in one row -->
        <div class="record-meta">
          <div *ngIf="i === 0 || patientID.isExisting"
               class="record-badge"
               [class.existing-badge]="patientID.isExisting"
               [class.primary-badge]="i === 0">
            <mat-icon class="badge-icon">
              {{i === 0 ? 'star' : 'lock'}}
            </mat-icon>
            <span class="badge-text">
              {{i === 0 ? 'Primary Record' : 'Existing Record'}}
            </span>
          </div>
          <button *ngIf="canRemoveMedicalRecord(i)"
                  class="remove-button"
                  color="warn"
                  type="button"
                  mat-icon-button
                  matTooltip="Remove this medical record number"
                  (click)="removeMedicalRecordNumber(i)">
            <mat-icon>delete</mat-icon>
          </button>
        </div>

        <!-- Fields Container -->
        <div class="fields-row"
             fxLayout="row"
             fxLayout.lt-md="column"
             fxLayoutGap="16px">
          <!-- Patient ID Field -->
          <div class="field-container"
               fxFlex>
            <mat-form-field class="medical-record-field"
                            appearance="outline"
                            [class.readonly-field]="patientID.isExisting">
              <mat-label>
                <mat-icon class="field-icon">
                  badge
                </mat-icon>
                Patient ID
              </mat-label>
              <input required
                     matInput
                     [disabled]="patientID.isExisting"
                     [ngModelOptions]="{standalone: true}"
                     [(ngModel)]="patientID.number"
                     (ngModelChange)="onFormFieldChange()">
              <mat-hint *ngIf="i === 0 && !patientID.isExisting"
                        class="primary-hint">
                <mat-icon class="hint-icon">
                  star
                </mat-icon>
                Primary medical record number
              </mat-hint>
              <mat-hint *ngIf="patientID.isExisting"
                        class="readonly-hint">
                <mat-icon class="hint-icon">
                  lock
                </mat-icon>
                {{i === 0 ? 'Primary record - ' : ''}}Existing record - cannot be modified
              </mat-hint>
              <mat-error *ngIf="!patientID.number?.trim()">
                Patient ID is required
              </mat-error>
            </mat-form-field>
          </div>

          <!-- Facility Field -->
          <div class="field-container"
               fxFlex>
            <mat-form-field class="facility-field"
                            appearance="outline"
                            [class.readonly-field]="patientID.isExisting">
              <mat-label>
                Facility
              </mat-label>
              <mat-select required
                          [disabled]="patientID.isExisting"
                          [ngModelOptions]="{standalone: true}"
                          [(value)]="patientID.facility"
                          (selectionChange)="onFacilityChange($event.value, i); onFormFieldChange()">
                <mat-option *ngFor="let facility of getAvailableFacilities(i)"
                            [value]="facility.id">
                  {{ facility.name }}
                </mat-option>
                <mat-option *ngIf="getAvailableFacilities(i).length === 0"
                            disabled>
                  <mat-icon class="option-icon">
                    warning
                  </mat-icon>
                  No facilities available
                </mat-option>
              </mat-select>
              <mat-hint *ngIf="i === 0 && !patientID.isExisting"
                        class="primary-hint">
                <mat-icon class="hint-icon">
                  star
                </mat-icon>
                Primary facility
              </mat-hint>
              <mat-hint *ngIf="patientID.isExisting"
                        class="readonly-hint">
                <mat-icon class="hint-icon">
                  lock
                </mat-icon>
                {{i === 0 ? 'Primary facility - ' : ''}}Existing facility - cannot be modified
              </mat-hint>
              <mat-error *ngIf="!patientID.facility">
                Facility selection is required
              </mat-error>
              <mat-error *ngIf="patientID.facility && isFacilityAlreadySelected(patientID.facility, i)">
                This facility is already selected for another medical record
              </mat-error>
            </mat-form-field>
          </div>
        </div>

      </div>
    </div>
  </div>

  <!-- Auto-Forwarding Section -->
  <div *ngIf="isOwner && userFacility" class="form-section">
    <app-auto-forwarding-section
      [caseName]="data.caseName"
      [hubFacilityId]="userFacility">
    </app-auto-forwarding-section>
  </div>

  <!-- Actions -->
  <div class="form-actions"
       fxLayout="row"
       fxLayoutAlign="space-between center">
    <button *ngIf="isOwner"
            type="button"
            color="warn"
            mat-stroked-button
            matTooltip="Permanently delete this patient record"
            (click)="deletePatient()">
      <mat-icon>delete_forever</mat-icon>
      DELETE PATIENT
    </button>
    <div *ngIf="!isOwner"></div>
    <div fxLayout="row"
         fxLayoutGap="16px">
      <button type="button"
              mat-button
              (click)="dialogRef.close(0)">
        CANCEL
      </button>
      <button color="warn"
              type="submit"
              mat-flat-button
              [disabled]="!canSaveForm()">
        SAVE CHANGES
      </button>
    </div>
  </div>
</form>
